import React, { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useBranch } from "@/contexts/BranchContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Stethoscope, Send, UserCheck, CheckCircle2, Clock, Search } from "lucide-react";

const fmtTime = (iso) => {
  if (!iso) return "-";
  const d = new Date(iso);
  return isNaN(d.getTime()) ? "-" : d.toLocaleString("id-ID", { timeZone: "Asia/Jakarta", hour: "2-digit", minute: "2-digit" });
};

export default function Consultation() {
  const { user } = useAuth();
  const { branchId } = useBranch();
  const isDoctor = user?.role === "doctor";
  const isFO = ["fo_cs", "manager", "owner", "super_admin"].includes(user?.role);

  const [board, setBoard] = useState({ waiting: [], in_progress: [], doctors: [] });
  const [search, setSearch] = useState("");
  const [results, setResults] = useState([]);
  const [selPatient, setSelPatient] = useState(null);
  const [treatmentType, setTreatmentType] = useState("");
  const [busy, setBusy] = useState(false);

  const loadBoard = useCallback(() => {
    const params = branchId && branchId !== "all" ? { branch_id: branchId } : {};
    api.get("/consultations/board", { params }).then((r) => setBoard(r.data)).catch(() => {});
  }, [branchId]);

  useEffect(() => {
    loadBoard();
    const t = setInterval(loadBoard, 6000); // real-time refresh
    return () => clearInterval(t);
  }, [loadBoard]);

  useEffect(() => {
    if (!search.trim()) { setResults([]); return; }
    const t = setTimeout(() => {
      api.get("/patients", { params: { search: search.trim(), limit: 8 } }).then((r) => setResults(r.data)).catch(() => setResults([]));
    }, 300);
    return () => clearTimeout(t);
  }, [search]);

  const sendToConsultation = async () => {
    if (!selPatient) { toast.error("Pilih pasien dulu"); return; }
    if (!branchId || branchId === "all") { toast.error("Pilih cabang aktif dulu"); return; }
    setBusy(true);
    try {
      await api.post("/consultations", { patient_id: selPatient.id, branch_id: branchId, treatment_type: treatmentType });
      toast.success(`${selPatient.name} dikirim ke antrean konsultasi`);
      setSelPatient(null); setSearch(""); setResults([]); setTreatmentType("");
      loadBoard();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal mengirim konsultasi");
    } finally { setBusy(false); }
  };

  const accept = async (cid) => {
    try { await api.post(`/consultations/${cid}/accept`); toast.success("Pasien diterima"); loadBoard(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };
  const complete = async (cid) => {
    try { await api.post(`/consultations/${cid}/complete`); toast.success("Konsultasi selesai"); loadBoard(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Gagal"); }
  };

  return (
    <div className="space-y-6" data-testid="consultation-page">
      <div>
        <h1 className="font-heading text-3xl tracking-tight flex items-center gap-2"><Stethoscope className="h-7 w-7 text-indigo-600" /> Alur Konsultasi</h1>
        <p className="mt-1 text-sm text-muted-foreground">Kirim pasien ke dokter & pantau penanganan secara real-time.</p>
      </div>

      {isFO && (
        <Card data-testid="send-consultation-card">
          <CardHeader><CardTitle className="text-lg">Kirim Pasien ke Konsultasi</CardTitle><CardDescription>Cari pasien lalu kirim ke antrean dokter.</CardDescription></CardHeader>
          <CardContent className="space-y-3">
            <div className="grid gap-3 md:grid-cols-2">
              <div className="relative">
                <label className="mb-1 block text-xs text-muted-foreground">Cari Pasien (nama / no HP / alamat)</label>
                <div className="relative">
                  <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input data-testid="consult-patient-search" className="pl-8" placeholder="Ketik nama pasien..." value={selPatient ? selPatient.name : search} onChange={(e) => { setSelPatient(null); setSearch(e.target.value); }} />
                </div>
                {results.length > 0 && !selPatient && (
                  <div className="absolute z-50 mt-1 w-full max-h-60 overflow-y-auto rounded-md border border-border bg-popover shadow-lg" data-testid="consult-search-results">
                    {results.map((p) => (
                      <button key={p.id} type="button" data-testid={`consult-pick-${p.id}`} onClick={() => { setSelPatient(p); setResults([]); }} className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-secondary">
                        <span className="font-medium truncate">{p.name}</span>
                        <span className="text-xs text-muted-foreground">{p.phone}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <label className="mb-1 block text-xs text-muted-foreground">Jenis Tindakan / Keluhan</label>
                <Input data-testid="consult-treatment-type" placeholder="cth. Facial Acne / Konsultasi Jerawat" value={treatmentType} onChange={(e) => setTreatmentType(e.target.value)} />
              </div>
            </div>
            <Button data-testid="send-consultation-btn" onClick={sendToConsultation} disabled={busy || !selPatient}>
              <Send className="mr-1.5 h-4 w-4" /> Kirim ke Konsultasi
            </Button>
          </CardContent>
        </Card>
      )}

      <div className="grid gap-6 lg:grid-cols-2">
        <Card data-testid="consult-queue-card">
          <CardHeader><CardTitle className="text-lg flex items-center gap-2"><Clock className="h-5 w-5 text-amber-600" /> Antrean Menunggu ({board.waiting.length})</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {board.waiting.length === 0 && <p className="text-sm text-muted-foreground py-4 text-center">Tidak ada antrean.</p>}
            {board.waiting.map((c) => (
              <div key={c.id} className="flex items-center justify-between gap-2 rounded-md border border-border p-2.5" data-testid={`consult-waiting-${c.id}`}>
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">{c.patient_name} <span className="text-xs text-muted-foreground">· {fmtTime(c.created_at)}</span></div>
                  <div className="text-xs text-muted-foreground truncate">{c.treatment_type || "Konsultasi"} {c.complaint ? `— ${c.complaint}` : ""}</div>
                </div>
                {isDoctor && <Button size="sm" data-testid={`accept-consult-${c.id}`} onClick={() => accept(c.id)}><UserCheck className="mr-1 h-4 w-4" />Terima Pasien</Button>}
                {!isDoctor && <Badge variant="outline" className="text-amber-700">In Konsultasi</Badge>}
              </div>
            ))}
          </CardContent>
        </Card>

        <Card data-testid="consult-active-card">
          <CardHeader><CardTitle className="text-lg flex items-center gap-2"><Stethoscope className="h-5 w-5 text-emerald-600" /> Sedang Ditangani ({board.in_progress.length})</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {board.in_progress.length === 0 && <p className="text-sm text-muted-foreground py-4 text-center">Belum ada pasien ditangani.</p>}
            {board.in_progress.map((c) => (
              <div key={c.id} className="flex items-center justify-between gap-2 rounded-md border border-emerald-200 bg-emerald-50/40 p-2.5" data-testid={`consult-active-${c.id}`}>
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">{c.patient_name}</div>
                  <div className="text-xs text-muted-foreground truncate">{c.treatment_type || "Konsultasi"} · Dokter: {c.doctor_name || "-"}</div>
                </div>
                {isDoctor && <Button size="sm" variant="outline" data-testid={`complete-consult-${c.id}`} onClick={() => complete(c.id)}><CheckCircle2 className="mr-1 h-4 w-4" />Selesai</Button>}
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      <Card data-testid="doctor-monitoring-card">
        <CardHeader><CardTitle className="text-lg">Monitoring Dokter (Real-time)</CardTitle><CardDescription>Dokter aktif di cabang ini & pasien yang sedang ditangani.</CardDescription></CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Dokter</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Pasien Ditangani</TableHead>
                <TableHead>Jenis Tindakan</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {board.doctors.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-sm text-muted-foreground py-6">Tidak ada dokter aktif di cabang ini.</TableCell></TableRow>}
              {board.doctors.map((d, i) => (
                <TableRow key={i} data-testid={`monitor-doctor-${i}`}>
                  <TableCell className="font-medium">{d.doctor_name}</TableCell>
                  <TableCell>{d.status === "handling" ? <Badge className="bg-emerald-600">Menangani</Badge> : <Badge variant="outline" className="text-slate-600">Tersedia</Badge>}</TableCell>
                  <TableCell>{d.current_patient || "—"}</TableCell>
                  <TableCell>{d.current_treatment || "—"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
